import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { HarborResponse } from '@/types/harbor-management.types';
import { KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import NumberInput from '@/components/inputControls/NumberInput';
import { formatFinnishCoordinate } from '@/utils';


interface HarborRequestModalProps extends Omit<
  ModalProps,
  'children' | 'onSubmit'
> {
  onSubmit: (selectedHarborIds: string[], requestMessage: string) => void;
  disabled?: boolean;
}

const HarborRequestModal = ({
  open,
  onClose,
  onSubmit,
  disabled = false,
}: HarborRequestModalProps) => {
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([]);
  const [tempSelectedHarbors, setTempSelectedHarbors] = useState<
    HarborResponse[]
  >([]);
  const [requestMessage, setRequestMessage] = useState<string>('');
  const {
    pagination,
    setPagination,
    handleTotalPages,
    handleTotalItems,
    resetSearch,
    totalPages,
    totalItems,
  } = usePagination();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // showharborlisting this flag is used to show all harbor for listing pagination api no role based filtering is added if its true
  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize, // Use dynamic page size
      search: debouncedValue || undefined,
      showharborlisting: true,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue]
  );

  const {
    data: harborsData,
    isLoading,
    isFetching,
    isError,
  } = useGetHarborsQuery(queryParams, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (open) {
      setTempSelectedIds([]);
      setTempSelectedHarbors([]);
      setRequestMessage('');
    }
  }, [open]);

  const handleDebouncedSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (harborsData?.data) {
      const currentPageHarbors = harborsData.data.filter((harbor) =>
        tempSelectedIds.includes(harbor.id)
      );
      setTempSelectedHarbors((prev) => {
        const updatedHarbors = [
          ...prev.filter((h) => !harborsData.data.some((d) => d.id === h.id)),
          ...currentPageHarbors,
        ];
        return updatedHarbors;
      });
    }
  }, [harborsData, tempSelectedIds]);

  useEffect(() => {
    if (harborsData) {
      const totalPages =
        harborsData.total_pages ||
        harborsData.totalPages ||
        Math.ceil(
          (harborsData.totalItems || harborsData.data?.length || 0) /
            pagination.pageSize
        );
      const totalItems =
        harborsData.totalItems ||
        harborsData.total ||
        harborsData.data?.length ||
        0;

      handleTotalPages(totalPages);
      handleTotalItems(totalItems);
    }
  }, [
    harborsData,
    handleTotalPages,
    handleTotalItems,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  const handleSelectHarbor = useCallback(
    (harborId: string) => {
      // Find the harbor data
      const harbor = harborsData?.data?.find((h) => h.id === harborId);
      if (!harbor) {
        return;
      }

      setTempSelectedIds((prev) => {
        const isCurrentlySelected = prev.includes(harborId);
        return isCurrentlySelected
          ? prev.filter((id) => id !== harborId)
          : [...prev, harborId];
      });

      // Update tempSelectedHarbors as well
      setTempSelectedHarbors((prev) => {
        const isCurrentlySelected = prev.some((h) => h.id === harborId);
        if (isCurrentlySelected) {
          // Remove harbor
          return prev.filter((h) => h.id !== harborId);
        } else {
          // Add harbor
          return [...prev, harbor];
        }
      });
    },
    [harborsData]
  );

  const handleRemoveHarbor = useCallback((harborId: string) => {
    setTempSelectedIds((prev) => prev.filter((id) => id !== harborId));
    setTempSelectedHarbors((prev) =>
      prev.filter((harbor) => harbor.id !== harborId)
    );
  }, []);

  const handleClearAll = useCallback(() => {
    setTempSelectedIds([]);
    setTempSelectedHarbors([]);
    setSearchTerm('');
    setDebouncedValue('');
    setRequestMessage('');
    resetSearch();
  }, [resetSearch]);

  const handleBack = useCallback(() => {
    onClose?.({}, 'backdropClick');
  }, [onClose]);

  const handleSave = useCallback(() => {
    onSubmit(tempSelectedIds, requestMessage);
  }, [onSubmit, tempSelectedIds, requestMessage]);

  // Handle select all functionality
  const handleSelectAll = useCallback(() => {
    if (
      harborsData?.data?.length &&
      harborsData.data.every((harbor) => tempSelectedIds.includes(harbor.id))
    ) {
      // Deselect all current page items
      setTempSelectedIds((prev) =>
        prev.filter(
          (id) => !harborsData.data.some((harbor) => harbor.id === id)
        )
      );
      setTempSelectedHarbors((prev) =>
        prev.filter(
          (harbor) => !harborsData.data.some((h) => h.id === harbor.id)
        )
      );
    } else {
      // Select all current page items
      setTempSelectedIds((prev) => [
        ...prev,
        ...(harborsData?.data
          ?.filter((harbor) => !prev.includes(harbor.id))
          .map((harbor) => harbor.id) || []),
      ]);
      setTempSelectedHarbors((prev) => [
        ...prev,
        ...(harborsData?.data?.filter(
          (harbor) => !prev.some((h) => h.id === harbor.id)
        ) || []),
      ]);
    }
  }, [harborsData, tempSelectedIds]);

  return (
    <>
      <style>{`
        .harbor-scroll-container::-webkit-scrollbar {
          width: 6px;
        }
        
        .harbor-scroll-container::-webkit-scrollbar-track {
          background: #f7fafc;
          border-radius: 3px;
        }
        
        .harbor-scroll-container::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }
        
        .harbor-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
        
        /* Ensure smooth scrolling */
        .harbor-scroll-container {
          scroll-behavior: smooth;
        }
        
        /* Mobile touch scrolling */
        .harbor-scroll-container {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
      <Modal
        open={open}
        onClose={handleBack}
        className="!flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
        aria-labelledby="harbor-request-title"
        aria-describedby="harbor-request-description"
      >
        <div
          className="bg-gray-100 p-3 sm:p-4 md:p-6 w-full max-w-7xl mx-auto rounded-lg shadow-lg flex flex-col max-h-[95vh] min-h-0 sm:min-h-[70vh] overflow-y-auto lg:overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="harbor-request-title"
          aria-describedby="harbor-request-description"
        >
          <h1
            id="harbor-request-title"
            className="text-lg sm:text-xl font-medium text-gray-900 mb-4"
          >
            Harbor Request
          </h1>
          <p id="harbor-request-description" className="sr-only">
            Select harbors and provide a request message to submit your harbor
            association request
          </p>
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-6 flex-1 overflow-y-auto lg:overflow-hidden min-h-0">
            {/* Left Panel - Form and Selected Harbors */}
            <div className="flex flex-col gap-3 sm:gap-4 lg:w-1/2 min-w-0 order-2 lg:order-1">
              {/* Search and Clear Controls */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <div className="w-full sm:max-w-xs">
                  <label
                    htmlFor="harbor-search"
                    className="block text-sm font-medium text-gray-700 mb-1 sm:sr-only"
                    aria-label="Search harbors"
                  >
                    Search Harbors
                  </label>
                  <div className="input input-sm h-8 flex items-center w-full">
                    <KeenIcon
                      type="filled"
                      icon="magnifier"
                      className="text-gray-500"
                      aria-hidden="true"
                    />
                    <input
                      id="harbor-search"
                      ref={searchInputRef}
                      placeholder="Search harbors"
                      type="text"
                      value={searchTerm}
                      onChange={handleDebouncedSearch}
                      className="w-full h-8 text-sm"
                      aria-describedby="search-help"
                      disabled={isLoading || isFetching}
                    />
                  </div>
                  <div id="search-help" className="sr-only">
                    Type to search for harbors by name
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-light flex items-center gap-2 h-8 w-full sm:w-auto"
                  onClick={handleClearAll}
                  disabled={!tempSelectedIds.length}
                  aria-describedby="clear-help"
                >
                  <KeenIcon
                    type="solid"
                    icon="arrows-circle"
                    className="text-gray-600"
                    aria-hidden="true"
                  />
                  Clear All
                </button>
                <div id="clear-help" className="sr-only">
                  Remove all selected harbors from your request
                </div>
              </div>

              {/* Request Message Field */}
              <div className="w-full">
                <label
                  htmlFor="request-message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Request Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="request-message"
                  className="w-full bg-light-active border border-gray-300 rounded-md p-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  placeholder="Enter your request message here..."
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  aria-describedby="message-help"
                  required
                />
                <div id="message-help" className="text-xs text-gray-500 mt-1">
                  Provide details about your harbor association request
                </div>
              </div>

              {/* Selected Harbors */}
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <h2 className="text-sm font-medium text-gray-700">
                    Selected Harbors ({tempSelectedHarbors.length})
                  </h2>
                  {tempSelectedHarbors.length > 0 && (
                    <button
                      onClick={() => {
                        setTempSelectedIds([]);
                        setTempSelectedHarbors([]);
                      }}
                      className="text-xs text-red-600 hover:text-red-800 underline flex-shrink-0"
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
                <div
                  data-testid="selected-harbors"
                  className="harbor-scroll-container flex-1 overflow-y-auto border rounded p-2 sm:p-3 bg-gray-100 min-h-[120px] max-h-[200px] sm:max-h-[250px] md:max-h-[300px] lg:max-h-[350px] xl:max-h-[400px]"
                  role="region"
                  aria-label="Selected harbors list"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 #f7fafc',
                  }}
                >
                  {tempSelectedHarbors.length > 0 ? (
                    <div className="space-y-2">
                      {tempSelectedHarbors.map((harbor) => (
                        <div
                          key={harbor.id}
                          className="flex items-center justify-between bg-gray-100 border rounded-lg px-2 sm:px-3 py-2 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex-1 min-w-0 pr-2">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {harbor.name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {formatFinnishCoordinate(harbor.latitude)},{' '}
                              {formatFinnishCoordinate(harbor.longitude)}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveHarbor(harbor.id)}
                            className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded min-w-[36px] min-h-[36px] flex items-center justify-center"
                            aria-label={`Remove ${harbor.name} from selection`}
                            type="button"
                          >
                            <KeenIcon
                              type="filled"
                              icon="cross"
                              className="w-4 h-4"
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <div className="text-2xl mb-2">📋</div>
                        <div className="text-sm">No harbors selected</div>
                        <div className="text-xs mt-1">
                          Select harbors from the list on the right
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Available Harbors */}
            <div className="flex-1 lg:w-1/2 min-h-0 order-1 lg:order-2 min-h-[300px] lg:min-h-0 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-medium text-gray-700">
                  Available Harbors
                </h2>
                <div className="flex items-center gap-2">
                  {harborsData?.data && harborsData.data.length > 0 && (
                    <button
                      onClick={handleSelectAll}
                      className="text-xs text-blue-600 hover:text-blue-800 underline contrast:text-blue-400"
                      aria-label={
                        harborsData.data.every((harbor) =>
                          tempSelectedIds.includes(harbor.id)
                        )
                          ? 'Deselect all harbors on this page'
                          : 'Select all harbors on this page'
                      }
                    >
                      {harborsData.data.every((harbor) =>
                        tempSelectedIds.includes(harbor.id)
                      )
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                  )}
                  {harborsData?.data && (
                    <span className="text-xs text-gray-500">
                      {harborsData.data.length} of {totalItems} harbors
                    </span>
                  )}
                </div>
              </div>

              {/* Enhanced Pagination Controls - Moved to top */}
              {harborsData?.data && harborsData.data.length > 0 && (
                <div className="flex flex-col gap-3 mb-4 p-3 bg-gray-100 border rounded-lg">
                  {/* Page Size Selector */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span>Show</span>
                      <select
                        id="page-size-select"
                        value={pagination.pageSize}
                        onChange={(e) =>
                          setPagination((prev) => ({
                            ...prev,
                            pageSize: parseInt(e.target.value),
                            pageIndex: 0, // Reset to first page when changing page size
                          }))
                        }
                        className="border border-gray-300 rounded px-2 py-1 text-xs bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        aria-label="Number of harbors to display per page"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={25}>25</option>
                      </select>
                      <span>harbors per page</span>
                    </div>
                    <div className="text-xs text-gray-600 font-medium">
                      Showing{' '}
                      {Math.min(
                        pagination.pageIndex * pagination.pageSize + 1,
                        totalItems || harborsData?.data?.length || 0
                      )}{' '}
                      -{' '}
                      {Math.min(
                        (pagination.pageIndex + 1) * pagination.pageSize,
                        totalItems || harborsData?.data?.length || 0
                      )}{' '}
                      of {totalItems || harborsData?.data?.length || 0} harbors
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    {/* Page Info and Direct Navigation */}
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="page-input"
                          className="text-xs text-gray-600"
                        >
                          Page:
                        </label>
                        <NumberInput
                          id="page-input"
                          min="1"
                          autoComplete="off"
                          max={Math.max(
                            totalPages,
                            Math.ceil(
                              (totalItems || harborsData?.data?.length || 0) /
                                pagination.pageSize
                            )
                          )}
                          value={pagination.pageIndex + 1}
                          onChange={(e) => {
                            const page = parseInt(e.target.value);
                            const maxPage = Math.max(
                              totalPages,
                              Math.ceil(
                                (totalItems || harborsData?.data?.length || 0) /
                                  pagination.pageSize
                              )
                            );
                            if (page >= 1 && page <= maxPage) {
                              setPagination((prev) => ({
                                ...prev,
                                pageIndex: page - 1,
                              }));
                            }
                          }}
                          className="w-12 border border-gray-300 rounded px-2 py-1 text-xs text-center bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span>
                          of{' '}
                          {Math.max(
                            totalPages,
                            Math.ceil(
                              (totalItems || harborsData?.data?.length || 0) /
                                pagination.pageSize
                            )
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Previous/Next Buttons */}
                    <div className="flex gap-1">
                      <button
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            pageIndex: Math.max(0, prev.pageIndex - 1),
                          }))
                        }
                        disabled={pagination.pageIndex === 0}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:shadow-sm active:bg-gray-100 min-w-[40px] min-h-[32px] flex items-center justify-center bg-gray-100 transition-all"
                        aria-label="Previous page"
                        title="Previous page"
                      >
                        <KeenIcon
                          icon="black-left"
                          className="text-gray-600 text-sm"
                        />
                      </button>
                      <button
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            pageIndex: Math.min(
                              Math.max(
                                totalPages,
                                Math.ceil(
                                  (totalItems ||
                                    harborsData?.data?.length ||
                                    0) / pagination.pageSize
                                )
                              ) - 1,
                              prev.pageIndex + 1
                            ),
                          }))
                        }
                        disabled={
                          pagination.pageIndex >=
                          Math.max(
                            totalPages,
                            Math.ceil(
                              (totalItems || harborsData?.data?.length || 0) /
                                pagination.pageSize
                            )
                          ) -
                            1
                        }
                        className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:shadow-sm active:bg-gray-100 min-w-[40px] min-h-[32px] flex items-center justify-center bg-gray-100 transition-all"
                        aria-label="Next page"
                        title="Next page"
                      >
                        <KeenIcon
                          icon="black-right"
                          className="text-gray-600 text-sm"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div
                className="harbor-scroll-container flex-1 overflow-y-auto space-y-2 pr-1 sm:pr-2 min-h-0"
                role="region"
                aria-label="Available harbors list"
                tabIndex={0}
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e0 #f7fafc',
                }}
              >
                {isLoading || isFetching ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">
                      Loading harbors...
                    </span>
                  </div>
                ) : isError ? (
                  <div className="text-center py-8">
                    <div className="text-red-500 mb-2">
                      ⚠️ Failed to load harbors
                    </div>
                    <button
                      onClick={() => window.location.reload()}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Try again
                    </button>
                  </div>
                ) : harborsData?.data?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="mb-2">🏠 No harbors found</div>
                    <div className="text-sm">
                      Try adjusting your search terms
                    </div>
                  </div>
                ) : (
                  <>
                    {harborsData?.data?.map((harbor) => (
                      <div
                        key={harbor.id}
                        className={`border rounded-lg p-2 sm:p-3 md:p-4 transition-all duration-200 cursor-pointer touch-manipulation min-h-[70px] sm:min-h-[80px] ${
                          tempSelectedIds.includes(harbor.id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950 shadow-lg ring-2 ring-blue-200 dark:ring-blue-800 contrast:bg-blue-950'
                            : 'border-gray-200 bg-gray-100 hover:border-gray-300 hover:shadow-md active:bg-gray-200'
                        }`}
                        onClick={() => handleSelectHarbor(harbor.id)}
                        aria-label={harbor.name}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectHarbor(harbor.id);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <label
                                htmlFor={`harbor-${harbor.id}`}
                                className="sr-only"
                              >
                                Select {harbor.name}
                              </label>
                              <input
                                id={`harbor-${harbor.id}`}
                                type="checkbox"
                                checked={tempSelectedIds.includes(harbor.id)}
                                className="checkbox checkbox-sm flex-shrink-0 cursor-pointer"
                                tabIndex={-1}
                                aria-hidden="true"
                              />
                              <h3 className="font-medium text-gray-900 text-sm sm:text-base leading-tight cursor-pointer">
                                {harbor.name}
                              </h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-xs text-gray-500 ml-7">
                              <div className="flex items-center">
                                <span className="font-medium mr-1">Lat:</span>
                                <span className="truncate">
                                  {formatFinnishCoordinate(harbor.latitude)}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <span className="font-medium mr-1">Lng:</span>
                                <span className="truncate">
                                  {formatFinnishCoordinate(harbor.longitude)}
                                </span>
                              </div>
                            </div>
                          </div>
                          {tempSelectedIds.includes(harbor.id) && (
                            <div
                              className="flex-shrink-0 text-blue-600 bg-blue-100 contrast:bg-blue-600 rounded-full p-1"
                              aria-hidden="true"
                            >
                              <KeenIcon
                                type="filled"
                                icon="check"
                                className="w-4 h-4"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-3 sm:mt-4 md:mt-6 flex flex-col sm:flex-row justify-between gap-2 sm:gap-3 pt-3 sm:pt-4 border-t flex-shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className="btn btn-sm btn-light w-full sm:w-auto order-2 sm:order-1"
              aria-describedby="back-help"
            >
              Back
            </button>
            <div id="back-help" className="sr-only">
              Cancel and close this dialog without submitting
            </div>

            <button
              type="button"
              onClick={handleSave}
              className="btn btn-sm btn-primary w-full sm:w-auto order-1 sm:order-2"
              disabled={
                disabled || !tempSelectedIds.length || !requestMessage.trim()
              }
              aria-describedby="submit-help"
            >
              Submit Request
              {disabled && (
                <span className="ml-2" aria-hidden="true">
                  <KeenIcon icon="spinner-two" className="animate-spin" />
                </span>
              )}
            </button>
            <div id="submit-help" className="sr-only">
              Submit your harbor association request with selected harbors and
              message
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default HarborRequestModal;
